/* eslint-disable react-hooks/exhaustive-deps */
import { LeftSquareFilled } from '@ant-design/icons';
import { Button, Input } from 'antd';
import clsx from 'classnames';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Inviter, Registerer, SessionState, UserAgent } from 'sip.js';
import {
  PHONE_REGISTER_STATUS,
  PHONE_STATES,
} from '../../constants/system.keys';
import { useOutsideComponent } from '../../hooks/useOutsideComponent';
import { Calling } from './Calling';
import HeaderCall from './HeaderCall';
import NoConfig from './NoConfig';
import { NumPad } from './NumPad';
import { RowPad } from './RowPad';

const DEFAULT_SIP_PORT = 7443;

export const DialPad = ({ open, accountSip, calledPhone, onClose }) => {
  const { t } = useTranslation();

  const remoteStream = new MediaStream();
  const [phone, setPhone] = useState(calledPhone);
  const [phoneState, setPhoneState] = useState(PHONE_STATES.NOT_SET);
  const [keys, setKeys] = useState((calledPhone || '').split(''));
  const [isValidConfig, setIsValidConfig] = useState(false);

  const [registerStatus, setRegisterStatus] = useState(
    PHONE_REGISTER_STATUS.UNSET
  );

  const [userAgent, setUserAgent] = useState(null);
  const [register, setRegister] = useState(null);

  const [outCallSession, setOutCallSession] = useState(null);
  const [isCalling, setIsCalling] = useState(false);

  const dialPadRef = useRef();
  const audioRef = useRef();

  useOutsideComponent(dialPadRef, () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  });

  const addKeys = (val) => {
    const newKeys = keys;
    newKeys.push(val);
    setKeys(newKeys);
    setPhone(newKeys.join(''));
  };

  const removeKey = () => {
    const newKeys = keys;
    newKeys.pop();
    setKeys(newKeys);
    setPhone(newKeys.join(''));
  };

  const setupRemoteMedia = (session) => {
    session.sessionDescriptionHandler.peerConnection
      .getReceivers()
      .forEach((receiver) => {
        if (receiver.track) {
          remoteStream.addTrack(receiver.track);
        }
      });

    if (audioRef.current) {
      audioRef.current.srcObject = remoteStream;
      audioRef.current.play();
    }
  };

  const cleanupMedia = () => {
    if (audioRef.current) {
      audioRef.current.srcObject = null;
      audioRef.current.pause();
    }
  };

  const registerSipCall = () => {
    if (accountSip && isValidConfig) {
      setRegisterStatus(PHONE_REGISTER_STATUS.REGISTERING);

      const uaConfig = {
        displayName: accountSip.username,
        uri: UserAgent.makeURI(
          `sip:${accountSip.username}@${accountSip.endpoint}:${
            accountSip.port || DEFAULT_SIP_PORT
          }`
        ),

        transportOptions: {
          server: `wss://${accountSip.endpoint}:${
            accountSip.port || DEFAULT_SIP_PORT
          }`,
          connectionTimeout: 100,
          keepAliveInterval: 20,
        },
        authorizationPassword: accountSip.password,
        authorizationUsername: accountSip.username,
        delegate: {
          onConnect: () => {
            console.log('====> onConnect');
            setRegisterStatus(PHONE_REGISTER_STATUS.REGISTERING);
          },
          onRegister: (r) => {
            console.log('====> onRegister');
          },
          onDisconnect: () => {
            console.log('====> onDisconnect');
          },
          onInvite: (invitation) => {
            console.log('====> Incoming call');
          },
        },
      };

      const ua = new UserAgent(uaConfig);
      const registerer = new Registerer(ua);

      // Add listen state change
      ua.transport.onConnect = () => {
        console.log('====> User Agent onConnect');
      };

      ua.transport.onDisconnect = () => {
        console.log('====> User Agent onDisconnect');
      };

      ua.start().then(() => {
        registerer.register({
          requestDelegate: {
            onAccept: (resp) => {
              console.log('Register is accepted: ', resp.message);
              setRegisterStatus(PHONE_REGISTER_STATUS.READY);
            },
            onReject: (response) => {
              console.log(' Register is rejected ===> ', response.message);
              setRegisterStatus(PHONE_REGISTER_STATUS.UNSET);
            },
          },
        });
      });

      setUserAgent(ua);
      setRegister(registerer);
    }
  };

  const callBtnPressed = () => {
    if (!userAgent) {
      registerSipCall();
    }

    const target = UserAgent.makeURI(
      `sip:${phone}@${accountSip.endpoint}:${DEFAULT_SIP_PORT}`
    );
    if (!target) {
      console.error('Failed to create target URI.');
      return;
    }

    const inviter = new Inviter(userAgent, target, {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: true,
          video: false,
        },
      },
    });

    inviter.stateChange.addListener((newState) => {
      switch (newState) {
        case SessionState.Establishing:
          console.log('==> Ringing call');
          setPhoneState(PHONE_STATES.RINGING);
          setIsCalling(true);
          break;
        case SessionState.Established:
          console.log('===> Call Established');
          setPhoneState(PHONE_STATES.IN_CALL);
          setIsCalling(false);
          setupRemoteMedia(inviter);
          break;

        case SessionState.Terminated:
          console.log('===> Call Terminated');
          setPhoneState(PHONE_STATES.TERMINATED);
          setIsCalling(false);
          cleanupMedia();
          break;
        default:
          setPhoneState(PHONE_STATES.NOT_SET);
          break;
      }
    });

    inviter
      .invite()
      .then((res) => {
        setPhoneState(PHONE_STATES.CALLING_OUT);
        setIsCalling(true);
        console.log(`Calling out: ${inviter.remoteIdentity.uri.user}`);
      })
      .catch((err) => {
        console.log('Call has rejected: ', err);
        setPhoneState(PHONE_STATES.REJECTED);
        setIsCalling(false);
      });

    setOutCallSession(inviter);
  };

  const terminatedCall = () => {
    if (outCallSession) {
      if (outCallSession.state === SessionState.Established) {
        outCallSession.bye();
      } else {
        outCallSession.cancel();
      }
    }
  };

  const clearRegisterCall = () => {
    terminatedCall();
    if (register) {
      register.unregister();
    }

    if (userAgent) {
      userAgent.stop();
    }

    setRegister(null);
    setUserAgent(null);
    setOutCallSession(null);
  };

  const keyPress = useCallback((event) => {
    if (event.key === 'Backspace') {
      removeKey();
    } else {
      if (
        ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'].indexOf(
          event.key
        ) !== -1
      ) {
        addKeys(event.key);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      clearRegisterCall();
    };
  }, []);

  useEffect(() => {
    if (open) {
      registerSipCall();
    } else {
      clearRegisterCall();
    }
  }, [open, isValidConfig]);

  useEffect(() => {
    if (!accountSip) {
      setIsValidConfig(false);
    } else {
      if (
        !accountSip?.endpoint ||
        !accountSip?.username ||
        !accountSip?.password
      ) {
        setIsValidConfig(false);
      } else {
        setIsValidConfig(true);
      }
    }
  }, [accountSip]);

  useEffect(() => {
    if (calledPhone) {
      setPhone(calledPhone);
    }
  }, [calledPhone]);

  useEffect(() => {
    document.addEventListener('keydown', keyPress, false);

    return () => {
      document.removeEventListener('keydown', keyPress, false);
    };
  }, [keyPress]);

  return (
    <div
      className={clsx('dial-pad-wrapper', {
        'dial-pad-show': open,
        'dial-pad-hidden': !open,
      })}
      ref={dialPadRef}
    >
      <audio ref={audioRef} autoPlay={true}></audio>
      {phoneState === PHONE_STATES.CALLING_OUT ||
      phoneState === PHONE_STATES.IN_CALL ? (
        <Calling
          phone={phone}
          phoneState={phoneState}
          onCancelCall={() => {
            terminatedCall();
          }}
        />
      ) : (
        <div className="key-pad">
          {!isValidConfig ? (
            <NoConfig />
          ) : (
            <Fragment>
              <HeaderCall
                username={accountSip.username}
                status={registerStatus}
              />
              <div className="input-phone">
                <Input
                  className="text-input"
                  value={phone}
                  variant="outlined"
                  size="small"
                />
                <LeftSquareFilled
                  style={{ fontSize: 20 }}
                  onClick={removeKey}
                />
              </div>
              <RowPad>
                <NumPad
                  onClick={addKeys}
                  value="1"
                  title="1"
                  subtitle=""
                  className="one-item"
                />
                <NumPad onClick={addKeys} value="2" subtitle="ABC" title="2" />
                <NumPad onClick={addKeys} value="3" subtitle="DEF" title="3" />
              </RowPad>
              <RowPad>
                <NumPad onClick={addKeys} value="4" subtitle="GHI" title="4" />
                <NumPad onClick={addKeys} value="5" subtitle="JKL" title="5" />
                <NumPad onClick={addKeys} value="6" subtitle="MNO" title="6" />
              </RowPad>
              <RowPad>
                <NumPad onClick={addKeys} value="7" subtitle="PQRS" title="7" />
                <NumPad onClick={addKeys} value="8" subtitle="YUV" title="8" />
                <NumPad onClick={addKeys} value="9" subtitle="WXYZ" title="9" />
              </RowPad>
              <RowPad>
                <NumPad onClick={addKeys} value="*" title="*" subtitle="" />
                <NumPad onClick={addKeys} value="0" title="0" subtitle="" />
                <NumPad onClick={addKeys} value="#" title="#" subtitle="" />
              </RowPad>

              {!userAgent && (
                <Button
                  type="primary"
                  style={{ width: '100%' }}
                  disabled={phone.length === 0}
                  onClick={() => {
                    registerSipCall();
                  }}
                >
                  {registerStatus === PHONE_REGISTER_STATUS.REGISTERING
                    ? t('Registering')
                    : t('Register Call')}
                </Button>
              )}

              {userAgent && (
                <Button
                  type="primary"
                  style={{ width: '100%' }}
                  disabled={
                    phone.length === 0 ||
                    isCalling ||
                    registerStatus !== PHONE_REGISTER_STATUS.READY
                  }
                  onClick={() => {
                    callBtnPressed();
                  }}
                >
                  {t('Call')}
                </Button>
              )}
            </Fragment>
          )}
        </div>
      )}
    </div>
  );
};

export default DialPad;
